/* Press Board (Test) — Rugtales
   Apple-Books-style library with reader.
   - Vertical-scrolling cover grid (CSS handles layout)
   - Click cover → fullscreen reader
   - Reader: keyboard, swipe, multi-touch safe (pinch-zoom)
   - Re-inits on shopify:section:load */
(function () {
  'use strict';

  const SWIPE_THRESHOLD = 50;
  const SWIPE_MAX_VERTICAL = 80;

  class PressBoard {
    constructor(root) {
      this.root = root;
      this.data = this.parseData();
      this.reader = root.querySelector('[data-press-board-reader]');
      this.imgEl = root.querySelector('[data-press-board-image]');
      this.magazineEl = root.querySelector('[data-press-board-magazine]');
      this.issueEl = root.querySelector('[data-press-board-issue]');
      this.currentEl = root.querySelector('[data-press-board-current]');
      this.totalEl = root.querySelector('[data-press-board-total]');
      this.prevBtn = root.querySelector('[data-press-board-prev]');
      this.nextBtn = root.querySelector('[data-press-board-next]');

      this.activeId = null;
      this.activeIndex = 0;
      this.lastFocused = null;

      this.bindEvents();
    }

    parseData() {
      const tag = this.root.querySelector('script[data-press-board-data]');
      if (!tag) return [];
      try { return JSON.parse(tag.textContent); }
      catch (err) { console.warn('[press-board] parse error', err); return []; }
    }

    bindEvents() {
      this.root.querySelectorAll('[data-press-board-open]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(btn.getAttribute('data-press-board-open'));
        });
      });

      // Year-row horizontal scroll arrows
      this.root.querySelectorAll('[data-press-board-scroll]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const dir = btn.getAttribute('data-press-board-scroll');
          const year = btn.getAttribute('data-target-year');
          const rail = this.root.querySelector(`[data-press-board-rail="${year}"]`);
          if (!rail) return;
          const step = rail.clientWidth * 0.7;
          rail.scrollBy({ left: dir === 'next' ? step : -step, behavior: 'smooth' });
        });
      });

      if (!this.reader) return;

      this.root.querySelectorAll('[data-press-board-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });
      this.reader.addEventListener('click', (e) => {
        if (e.target === this.reader) this.close();
      });
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

      this.keyHandler = (e) => {
        if (!this.isOpen()) return;
        if (e.key === 'Escape') { e.preventDefault(); this.close(); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
      };
      document.addEventListener('keydown', this.keyHandler);

      // Touch swipe (multi-touch / pinch-zoom safe)
      const wrap = this.root.querySelector('[data-press-board-image-wrap]');
      if (wrap) {
        let startX = 0, startY = 0, multi = false;
        wrap.addEventListener('touchstart', (e) => {
          if (e.touches.length > 1) { multi = true; return; }
          multi = false;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
        }, { passive: true });
        wrap.addEventListener('touchend', (e) => {
          if (multi) { multi = false; return; }
          if (window.visualViewport && window.visualViewport.scale > 1.05) return;
          const t = e.changedTouches[0];
          if (!t) return;
          const dx = t.clientX - startX;
          const dy = t.clientY - startY;
          if (Math.abs(dy) > SWIPE_MAX_VERTICAL) return;
          if (Math.abs(dx) < SWIPE_THRESHOLD) return;
          if (dx < 0) this.next(); else this.prev();
        }, { passive: true });
      }
    }

    findById(id) {
      return this.data.find((d) => String(d.id) === String(id));
    }

    open(id) {
      const item = this.findById(id);
      if (!item || !item.pages || item.pages.length === 0) return;
      this.activeId = id;
      this.activeIndex = 0;
      this.lastFocused = document.activeElement;

      this.magazineEl.textContent = item.magazine || '';
      this.issueEl.textContent = item.issue || '';
      this.totalEl.textContent = item.pages.length;

      this.renderPage();

      this.reader.hidden = false;
      // force reflow before adding class for transition
      // eslint-disable-next-line no-unused-expressions
      this.reader.offsetHeight;
      this.reader.classList.add('is-open');
      this.reader.setAttribute('aria-hidden', 'false');
      document.body.classList.add('press-board-open');

      const closeBtn = this.reader.querySelector('[data-press-board-close]');
      if (closeBtn) closeBtn.focus();
    }

    close() {
      if (!this.isOpen()) return;
      this.reader.classList.remove('is-open');
      this.reader.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('press-board-open');

      setTimeout(() => {
        if (!this.reader.classList.contains('is-open')) {
          this.reader.hidden = true;
        }
      }, 260);

      this.activeId = null;
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    isOpen() {
      return this.reader && this.reader.classList.contains('is-open');
    }

    renderPage() {
      const item = this.findById(this.activeId);
      if (!item) return;
      const total = item.pages.length;
      const src = item.pages[this.activeIndex];

      this.imgEl.classList.add('is-loading');
      const onLoad = () => {
        this.imgEl.classList.remove('is-loading');
        this.imgEl.removeEventListener('load', onLoad);
      };
      this.imgEl.addEventListener('load', onLoad);
      this.imgEl.src = src;
      this.imgEl.alt = `${item.magazine} ${item.issue} – Seite ${this.activeIndex + 1}`;

      this.currentEl.textContent = this.activeIndex + 1;
      if (this.prevBtn) this.prevBtn.disabled = this.activeIndex === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.activeIndex === total - 1;
    }

    next() {
      const item = this.findById(this.activeId);
      if (!item) return;
      if (this.activeIndex >= item.pages.length - 1) return;
      this.activeIndex += 1;
      this.renderPage();
    }
    prev() {
      if (this.activeIndex <= 0) return;
      this.activeIndex -= 1;
      this.renderPage();
    }

    destroy() {
      if (this.keyHandler) document.removeEventListener('keydown', this.keyHandler);
      document.body.classList.remove('press-board-open');
    }
  }

  const instances = new WeakMap();

  function init(scope) {
    const root = scope && scope.matches && scope.matches('[data-press-board]')
      ? scope
      : (scope || document).querySelector('[data-press-board]');
    if (!root) return;
    if (instances.has(root)) instances.get(root).destroy();
    instances.set(root, new PressBoard(root));
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-press-board]').forEach((el) => {
      if (instances.has(el)) instances.get(el).destroy();
      instances.set(el, new PressBoard(el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector ? e.target.querySelector('[data-press-board]') : null;
    if (root && instances.has(root)) instances.get(root).destroy();
  });
})();
